/**
 * Specialty-aware demo data for Lumina Clinical Focus.
 * ───────────────────────────────────────────────────────
 * Swaps the schedule list AND augments patient records with
 * MA-entered intake values (growth, screeners, OB math,
 * pre-op risk, etc.) when a specialty is selected from the
 * top-bar Specialty Switcher.
 *
 * All data is MOCK — surface only, for demo flows. The real-
 * mode path continues to pull from the FHIR/EHR layer.
 *
 * DEMO_FIXTURES_ONLY — consumers gate import/use behind isDemo.
 */
/* DEMO_FIXTURES_ONLY */

// ── Helper: convert months-old to a believable DOB string ──
const dobFromAgeYears = (yrs, monthOffset = 0) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - yrs);
  d.setMonth(d.getMonth() - monthOffset);
  return d.toISOString().slice(0, 10);
};

// ════════════════════════════════════════════════════════════════
// PSYCHIATRY / MENTAL HEALTH
// ════════════════════════════════════════════════════════════════
export const PSYCH_SCHEDULE = [
  { id: 'PS-001', patientId: 'PS001', name: 'Rachel Hayes', time: '9:00 AM', type: 'Follow-up', chief: 'MDD — med management, sertraline response check', status: 'completed' },
  { id: 'PS-002', patientId: 'PS002', name: 'Daniel Price', time: '9:45 AM', type: 'New Patient', chief: 'Generalized anxiety + panic attacks, 6 mo history', status: 'completed' },
  { id: 'PS-003', patientId: 'PS003', name: 'Monica Alvarez', time: '10:30 AM', type: 'Follow-up', chief: 'Bipolar II — mood stabilization, lamotrigine titration', status: 'in_progress' },
  { id: 'PS-004', patientId: 'PS004', name: 'Evan Brooks', time: '11:15 AM', type: 'Follow-up', chief: 'PTSD — trauma-focused CBT session', status: 'waiting' },
  { id: 'PS-005', patientId: 'PS005', name: 'Laura Chen', time: '1:00 PM', type: 'Sick Visit', chief: 'Worsening depression, passive SI reported by family', status: 'scheduled' },
  { id: 'PS-006', patientId: 'PS006', name: 'Marcus Reilly', time: '1:45 PM', type: 'Follow-up', chief: 'ADHD — stimulant re-evaluation', status: 'scheduled' },
  { id: 'PS-007', patientId: 'PS007', name: 'Ayesha Khan', time: '2:30 PM', type: 'Follow-up', chief: 'OCD — exposure therapy progress check', status: 'scheduled' },
  { id: 'PS-008', patientId: 'PS008', name: 'Thomas Vaughn', time: '3:15 PM', type: 'New Patient', chief: 'Sleep disturbance + low mood, r/o MDD', status: 'scheduled' },
];

const PSYCH_PATIENTS = {
  PS001: {
    firstName: 'Rachel', lastName: 'Hayes', age: 34, gender: 'Female', dob: dobFromAgeYears(34),
    mrn: 'MRN-PS001', insurance: 'Aetna PPO', memberId: 'AET-PS001',
    phone: '(555) 201-1100', email: 'rachel.h@example.com', address: '—',
    pcp: 'Dr. Sarah Chen', preferredPharmacy: 'CVS',
    vitals: { bp: '118/74', hr: '72', temp: '98.4', spo2: '99', weight: '142 lbs', bmi: '23.6' },
    problems: [{ name: 'Major Depressive Disorder, recurrent', icd: 'F33.1', status: 'Active', onset: '2023-02-01' }],
    medications: [{ name: 'Sertraline 100mg', freq: 'Daily', prescriber: 'Dr. Patel', startDate: '2024-05-01' }],
    allergies: [], labs: [],
    psychIntake: { phq9: 14, gad7: 9, cssrs: 1, siRisk: 'low', lastSession: '2 wks ago', adherence: 'Good', safetyPlan: 'Reviewed 2024-12-10' },
  },
  PS002: {
    firstName: 'Daniel', lastName: 'Price', age: 28, gender: 'Male', dob: dobFromAgeYears(28),
    mrn: 'MRN-PS002', insurance: 'UHC', phone: '(555) 201-1200', pcp: 'Dr. Patel',
    vitals: { bp: '128/82', hr: '88', temp: '98.6', spo2: '99', weight: '180 lbs', bmi: '25.8' },
    problems: [{ name: 'Generalized Anxiety Disorder', icd: 'F41.1', status: 'Active', onset: '2024-09-01' }],
    medications: [],
    psychIntake: { phq9: 6, gad7: 17, cssrs: 0, siRisk: 'none', lastSession: 'First visit', adherence: '—', safetyPlan: 'Not yet established' },
  },
  PS003: {
    firstName: 'Monica', lastName: 'Alvarez', age: 41, gender: 'Female', dob: dobFromAgeYears(41),
    mrn: 'MRN-PS003', insurance: 'BCBS', pcp: 'Dr. Patel',
    vitals: { bp: '124/80', hr: '76', temp: '98.5', spo2: '98', weight: '155 lbs', bmi: '25.1' },
    problems: [{ name: 'Bipolar II Disorder', icd: 'F31.81', status: 'Active', onset: '2019-06-01' }],
    medications: [{ name: 'Lamotrigine 100mg', freq: 'BID', prescriber: 'Dr. Patel' }],
    psychIntake: { phq9: 11, gad7: 7, cssrs: 2, siRisk: 'passive', lastSession: '3 wks ago', adherence: 'Fair', safetyPlan: 'Updated today' },
  },
  PS004: {
    firstName: 'Evan', lastName: 'Brooks', age: 36, gender: 'Male', dob: dobFromAgeYears(36),
    mrn: 'MRN-PS004', insurance: 'VA', pcp: 'Dr. Patel',
    vitals: { bp: '132/86', hr: '84', temp: '98.6', spo2: '98', weight: '195 lbs', bmi: '27.3' },
    problems: [{ name: 'PTSD', icd: 'F43.10', status: 'Active', onset: '2020-03-01' }],
    medications: [{ name: 'Prazosin 2mg', freq: 'QHS', prescriber: 'Dr. Patel' }],
    psychIntake: { phq9: 13, gad7: 15, cssrs: 1, siRisk: 'low', lastSession: '1 wk ago', adherence: 'Good', safetyPlan: 'In place' },
  },
  PS005: {
    firstName: 'Laura', lastName: 'Chen', age: 29, gender: 'Female', dob: dobFromAgeYears(29),
    mrn: 'MRN-PS005', insurance: 'Aetna', pcp: 'Dr. Patel',
    vitals: { bp: '110/70', hr: '68', temp: '98.2', spo2: '99', weight: '128 lbs', bmi: '22.1' },
    problems: [{ name: 'MDD, severe without psychotic features', icd: 'F33.2', status: 'Active', onset: '2024-11-01' }],
    medications: [{ name: 'Fluoxetine 40mg', freq: 'Daily', prescriber: 'Dr. Patel' }],
    psychIntake: { phq9: 22, gad7: 14, cssrs: 3, siRisk: 'moderate', lastSession: '5 days ago', adherence: 'Poor', safetyPlan: 'URGENT — review today' },
  },
  PS006: {
    firstName: 'Marcus', lastName: 'Reilly', age: 22, gender: 'Male', dob: dobFromAgeYears(22),
    mrn: 'MRN-PS006', insurance: 'Student Health', pcp: 'Dr. Patel',
    vitals: { bp: '120/78', hr: '74', temp: '98.6', spo2: '99', weight: '165 lbs', bmi: '23.7' },
    problems: [{ name: 'ADHD, combined type', icd: 'F90.2', status: 'Active', onset: '2018-01-01' }],
    medications: [{ name: 'Adderall XR 30mg', freq: 'Daily AM', prescriber: 'Dr. Patel' }],
    psychIntake: { phq9: 4, gad7: 5, cssrs: 0, siRisk: 'none', lastSession: '3 mo ago', adherence: 'Good', safetyPlan: 'N/A' },
  },
  PS007: {
    firstName: 'Ayesha', lastName: 'Khan', age: 31, gender: 'Female', dob: dobFromAgeYears(31),
    mrn: 'MRN-PS007', insurance: 'Cigna', pcp: 'Dr. Patel',
    vitals: { bp: '115/75', hr: '72', temp: '98.4', spo2: '99', weight: '135 lbs', bmi: '22.8' },
    problems: [{ name: 'OCD', icd: 'F42.9', status: 'Active', onset: '2022-07-01' }],
    medications: [{ name: 'Fluvoxamine 200mg', freq: 'Daily', prescriber: 'Dr. Patel' }],
    psychIntake: { phq9: 7, gad7: 11, cssrs: 0, siRisk: 'none', lastSession: '2 wks ago', adherence: 'Good', safetyPlan: 'N/A' },
  },
  PS008: {
    firstName: 'Thomas', lastName: 'Vaughn', age: 52, gender: 'Male', dob: dobFromAgeYears(52),
    mrn: 'MRN-PS008', insurance: 'Medicare', pcp: 'Dr. Chen',
    vitals: { bp: '136/88', hr: '80', temp: '98.6', spo2: '97', weight: '210 lbs', bmi: '29.1' },
    problems: [{ name: 'Insomnia disorder', icd: 'G47.00', status: 'Active', onset: '2024-08-01' }],
    medications: [],
    psychIntake: { phq9: 12, gad7: 8, cssrs: 0, siRisk: 'none', lastSession: 'First visit', adherence: '—', safetyPlan: 'Not yet established' },
  },
};

// ════════════════════════════════════════════════════════════════
// PEDIATRICS
// ════════════════════════════════════════════════════════════════
export const PEDS_SCHEDULE = [
  { id: 'PD-001', patientId: 'PD001', name: 'Liam Foster', time: '9:00 AM', type: 'Well Child', chief: '2-month well-child check + immunizations', status: 'completed' },
  { id: 'PD-002', patientId: 'PD002', name: 'Ava Richardson', time: '9:30 AM', type: 'Well Child', chief: '18-month well-child + M-CHAT-R', status: 'completed' },
  { id: 'PD-003', patientId: 'PD003', name: 'Noah Patel', time: '10:00 AM', type: 'Sick Visit', chief: 'Fever x 2 days, runny nose, 3 yr old', status: 'in_progress' },
  { id: 'PD-004', patientId: 'PD004', name: 'Sophia Kim', time: '10:30 AM', type: 'Follow-up', chief: 'Asthma action plan — wheeze episode last week (age 7)', status: 'waiting' },
  { id: 'PD-005', patientId: 'PD005', name: 'Elijah Turner', time: '11:00 AM', type: 'Well Child', chief: '5-year kindergarten physical + vaccines', status: 'scheduled' },
  { id: 'PD-006', patientId: 'PD006', name: 'Mia Thompson', time: '11:30 AM', type: 'Follow-up', chief: 'ADHD med check (age 10)', status: 'scheduled' },
  { id: 'PD-007', patientId: 'PD007', name: 'Jackson Lee', time: '1:00 PM', type: 'Sports Physical', chief: 'Sports physical for high-school soccer (age 15)', status: 'scheduled' },
  { id: 'PD-008', patientId: 'PD008', name: 'Isabella Cruz', time: '1:30 PM', type: 'Well Child', chief: '12-month well-child + MMR/Var', status: 'scheduled' },
];

const PEDS_PATIENTS = {
  PD001: {
    firstName: 'Liam', lastName: 'Foster', age: 0, gender: 'Male', dob: dobFromAgeYears(0, 2),
    mrn: 'MRN-PD001', insurance: 'BCBS Family', phone: '(555) 301-1001', pcp: 'Dr. Rivera',
    vitals: { bp: '—', hr: '138', temp: '98.4', spo2: '99', weight: '5.2 kg', bmi: '—' },
    problems: [], medications: [], allergies: [], labs: [],
    pedsIntake: { ageMonths: 2, sex: 'male', weight_kg: 5.2, length_cm: 58.4, hc_cm: 39.1, milestones: 'On track (social smile, head control improving)', vaccinesDue: ['HepB #2', 'DTaP #1', 'Hib #1', 'PCV13 #1', 'IPV #1', 'Rotavirus #1'] },
  },
  PD002: {
    firstName: 'Ava', lastName: 'Richardson', age: 1, gender: 'Female', dob: dobFromAgeYears(1, 6),
    mrn: 'MRN-PD002', insurance: 'Aetna Family', pcp: 'Dr. Rivera',
    vitals: { bp: '—', hr: '108', temp: '98.6', spo2: '99', weight: '11.2 kg', bmi: '16.4' },
    problems: [], medications: [],
    pedsIntake: { ageMonths: 18, sex: 'female', weight_kg: 11.2, length_cm: 82.5, hc_cm: 47.3, milestones: 'Walks well, 6+ words, points to pictures', vaccinesDue: ['DTaP #4', 'Hib #4', 'HepA #1'] },
  },
  PD003: {
    firstName: 'Noah', lastName: 'Patel', age: 3, gender: 'Male', dob: dobFromAgeYears(3),
    mrn: 'MRN-PD003', insurance: 'UHC Family', pcp: 'Dr. Rivera',
    vitals: { bp: '100/62', hr: '118', temp: '101.8', spo2: '98', weight: '15.2 kg', bmi: '15.9' },
    problems: [{ name: 'Acute URI', icd: 'J06.9', status: 'Active', onset: '2 days ago' }],
    medications: [],
    pedsIntake: { ageMonths: 36, sex: 'male', weight_kg: 15.2, length_cm: 96.0, hc_cm: 49.5, milestones: 'Speech at 3-word sentences, age-appropriate', vaccinesDue: [] },
  },
  PD004: {
    firstName: 'Sophia', lastName: 'Kim', age: 7, gender: 'Female', dob: dobFromAgeYears(7),
    mrn: 'MRN-PD004', insurance: 'BCBS Family', pcp: 'Dr. Rivera',
    vitals: { bp: '102/66', hr: '94', temp: '98.4', spo2: '96', weight: '23.1 kg', bmi: '15.2' },
    problems: [{ name: 'Asthma, mild persistent', icd: 'J45.30', status: 'Active', onset: '2022-01-01' }],
    medications: [{ name: 'Albuterol inhaler', freq: 'PRN', prescriber: 'Dr. Rivera' }, { name: 'Fluticasone 44mcg', freq: 'BID', prescriber: 'Dr. Rivera' }],
    pedsIntake: { ageMonths: 84, sex: 'female', weight_kg: 23.1, length_cm: 122.0, hc_cm: null, milestones: 'Reading at grade level', vaccinesDue: [] },
  },
  PD005: {
    firstName: 'Elijah', lastName: 'Turner', age: 5, gender: 'Male', dob: dobFromAgeYears(5),
    mrn: 'MRN-PD005', insurance: 'Aetna Family', pcp: 'Dr. Rivera',
    vitals: { bp: '96/60', hr: '102', temp: '98.6', spo2: '99', weight: '19.5 kg', bmi: '15.5' },
    problems: [], medications: [],
    pedsIntake: { ageMonths: 60, sex: 'male', weight_kg: 19.5, length_cm: 112.0, hc_cm: null, milestones: 'Kindergarten-ready; speech, social, motor age-appropriate', vaccinesDue: ['DTaP #5', 'IPV #4', 'MMR #2', 'Varicella #2'] },
  },
  PD006: {
    firstName: 'Mia', lastName: 'Thompson', age: 10, gender: 'Female', dob: dobFromAgeYears(10),
    mrn: 'MRN-PD006', insurance: 'UHC Family', pcp: 'Dr. Rivera',
    vitals: { bp: '106/68', hr: '88', temp: '98.6', spo2: '99', weight: '34.2 kg', bmi: '17.4' },
    problems: [{ name: 'ADHD, combined type', icd: 'F90.2', status: 'Active', onset: '2022-09-01' }],
    medications: [{ name: 'Methylphenidate ER 27mg', freq: 'Daily AM', prescriber: 'Dr. Rivera' }],
    pedsIntake: { ageMonths: 120, sex: 'female', weight_kg: 34.2, length_cm: 140.0, hc_cm: null, milestones: 'Academic — on IEP, progressing', vaccinesDue: [] },
  },
  PD007: {
    firstName: 'Jackson', lastName: 'Lee', age: 15, gender: 'Male', dob: dobFromAgeYears(15),
    mrn: 'MRN-PD007', insurance: 'BCBS Family', pcp: 'Dr. Rivera',
    vitals: { bp: '118/72', hr: '66', temp: '98.4', spo2: '99', weight: '62.0 kg', bmi: '21.0' },
    problems: [], medications: [],
    pedsIntake: { ageMonths: 180, sex: 'male', weight_kg: 62.0, length_cm: 172.0, hc_cm: null, milestones: 'Adolescent — Tanner stage 4, academic on track', vaccinesDue: ['Tdap booster', 'HPV #3', 'MenACWY booster'] },
  },
  PD008: {
    firstName: 'Isabella', lastName: 'Cruz', age: 1, gender: 'Female', dob: dobFromAgeYears(1),
    mrn: 'MRN-PD008', insurance: 'Aetna Family', pcp: 'Dr. Rivera',
    vitals: { bp: '—', hr: '116', temp: '98.6', spo2: '99', weight: '9.8 kg', bmi: '16.1' },
    problems: [], medications: [],
    pedsIntake: { ageMonths: 12, sex: 'female', weight_kg: 9.8, length_cm: 76.0, hc_cm: 45.8, milestones: 'Pulls to stand, 2-3 words, waves bye', vaccinesDue: ['MMR #1', 'Varicella #1', 'HepA #1', 'PCV13 #4', 'Hib #4'] },
  },
};

// ════════════════════════════════════════════════════════════════
// OB/GYN
// ════════════════════════════════════════════════════════════════
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export const OBGYN_SCHEDULE = [
  { id: 'OB-001', patientId: 'OB001', name: 'Emma Sullivan', time: '9:00 AM', type: 'Prenatal', chief: 'Routine prenatal — 8 weeks, first OB visit', status: 'completed' },
  { id: 'OB-002', patientId: 'OB002', name: 'Jessica Wong', time: '9:30 AM', type: 'Prenatal', chief: 'Prenatal — 20 wk anatomy scan follow-up', status: 'completed' },
  { id: 'OB-003', patientId: 'OB003', name: 'Natalie Ford', time: '10:00 AM', type: 'Prenatal', chief: 'Prenatal — 28 wk, GCT + Tdap', status: 'in_progress' },
  { id: 'OB-004', patientId: 'OB004', name: 'Olivia Ramirez', time: '10:30 AM', type: 'Prenatal', chief: 'Prenatal — 36 wk, GBS swab', status: 'waiting' },
  { id: 'OB-005', patientId: 'OB005', name: 'Grace Nguyen', time: '11:00 AM', type: 'Annual', chief: 'Annual GYN exam + Pap', status: 'scheduled' },
  { id: 'OB-006', patientId: 'OB006', name: 'Hannah Walsh', time: '11:30 AM', type: 'Postpartum', chief: '6-week postpartum visit', status: 'scheduled' },
  { id: 'OB-007', patientId: 'OB007', name: 'Chloe Barrett', time: '1:00 PM', type: 'Sick Visit', chief: 'Abnormal bleeding, r/o fibroids', status: 'scheduled' },
  { id: 'OB-008', patientId: 'OB008', name: 'Priya Sharma', time: '1:30 PM', type: 'Consult', chief: 'Preconception counseling', status: 'scheduled' },
];

const OBGYN_PATIENTS = {
  OB001: {
    firstName: 'Emma', lastName: 'Sullivan', age: 30, gender: 'Female', dob: dobFromAgeYears(30),
    mrn: 'MRN-OB001', insurance: 'BCBS', pcp: 'Dr. Ahmed',
    vitals: { bp: '112/72', hr: '80', temp: '98.4', spo2: '99', weight: '135 lbs', bmi: '22.5' },
    problems: [{ name: 'Intrauterine pregnancy', icd: 'Z34.00', status: 'Active', onset: daysAgoStr(56) }],
    medications: [{ name: 'Prenatal vitamin', freq: 'Daily', prescriber: 'Dr. Ahmed' }],
    obgynIntake: { gravida: 1, para: 0, lmp: daysAgoStr(56), gaWeeks: 8, gaDays: 0, edd: daysAgoStr(-224), fundalHeight: null, fht: 160, gbs: 'pending', trimester: '1st' },
  },
  OB002: {
    firstName: 'Jessica', lastName: 'Wong', age: 33, gender: 'Female', dob: dobFromAgeYears(33),
    mrn: 'MRN-OB002', insurance: 'Aetna', pcp: 'Dr. Ahmed',
    vitals: { bp: '118/74', hr: '82', temp: '98.6', spo2: '99', weight: '148 lbs', bmi: '24.1' },
    problems: [{ name: 'Intrauterine pregnancy', icd: 'Z34.02', status: 'Active', onset: daysAgoStr(140) }],
    medications: [{ name: 'Prenatal vitamin', freq: 'Daily' }],
    obgynIntake: { gravida: 2, para: 1, lmp: daysAgoStr(140), gaWeeks: 20, gaDays: 0, edd: daysAgoStr(-140), fundalHeight: 20, fht: 152, gbs: 'pending', trimester: '2nd' },
  },
  OB003: {
    firstName: 'Natalie', lastName: 'Ford', age: 28, gender: 'Female', dob: dobFromAgeYears(28),
    mrn: 'MRN-OB003', insurance: 'UHC', pcp: 'Dr. Ahmed',
    vitals: { bp: '122/78', hr: '86', temp: '98.6', spo2: '99', weight: '158 lbs', bmi: '25.8' },
    problems: [{ name: 'Intrauterine pregnancy', icd: 'Z34.03', status: 'Active', onset: daysAgoStr(196) }],
    medications: [{ name: 'Prenatal vitamin', freq: 'Daily' }, { name: 'Iron supplement', freq: 'Daily' }],
    obgynIntake: { gravida: 2, para: 1, lmp: daysAgoStr(196), gaWeeks: 28, gaDays: 0, edd: daysAgoStr(-84), fundalHeight: 28, fht: 148, gbs: 'pending', trimester: '3rd' },
  },
  OB004: {
    firstName: 'Olivia', lastName: 'Ramirez', age: 31, gender: 'Female', dob: dobFromAgeYears(31),
    mrn: 'MRN-OB004', insurance: 'BCBS', pcp: 'Dr. Ahmed',
    vitals: { bp: '126/80', hr: '84', temp: '98.6', spo2: '99', weight: '172 lbs', bmi: '27.6' },
    problems: [{ name: 'Intrauterine pregnancy', icd: 'Z34.03', status: 'Active', onset: daysAgoStr(252) }],
    medications: [{ name: 'Prenatal vitamin', freq: 'Daily' }],
    obgynIntake: { gravida: 3, para: 2, lmp: daysAgoStr(252), gaWeeks: 36, gaDays: 0, edd: daysAgoStr(-28), fundalHeight: 36, fht: 144, gbs: 'pending', trimester: '3rd' },
  },
  OB005: {
    firstName: 'Grace', lastName: 'Nguyen', age: 42, gender: 'Female', dob: dobFromAgeYears(42),
    mrn: 'MRN-OB005', insurance: 'Cigna', pcp: 'Dr. Ahmed',
    vitals: { bp: '120/76', hr: '72', temp: '98.4', spo2: '99', weight: '140 lbs', bmi: '23.3' },
    problems: [],
    medications: [],
    obgynIntake: { gravida: 3, para: 3, lmp: daysAgoStr(14), gaWeeks: null, gaDays: null, edd: null, fundalHeight: null, fht: null, gbs: null, trimester: null, lastPap: '2023-06', papResult: 'Normal', mammo: 'Due' },
  },
  OB006: {
    firstName: 'Hannah', lastName: 'Walsh', age: 29, gender: 'Female', dob: dobFromAgeYears(29),
    mrn: 'MRN-OB006', insurance: 'Aetna', pcp: 'Dr. Ahmed',
    vitals: { bp: '116/74', hr: '78', temp: '98.6', spo2: '99', weight: '152 lbs', bmi: '25.3' },
    problems: [{ name: 'Postpartum status', icd: 'Z39.2', status: 'Active', onset: daysAgoStr(42) }],
    medications: [{ name: 'Prenatal vitamin', freq: 'Daily' }],
    obgynIntake: { gravida: 1, para: 1, lmp: null, gaWeeks: null, gaDays: null, edd: null, fundalHeight: null, fht: null, gbs: 'negative', trimester: 'postpartum', deliveryDate: daysAgoStr(42), deliveryType: 'SVD', breastfeeding: 'Exclusive' },
  },
  OB007: {
    firstName: 'Chloe', lastName: 'Barrett', age: 38, gender: 'Female', dob: dobFromAgeYears(38),
    mrn: 'MRN-OB007', insurance: 'BCBS', pcp: 'Dr. Ahmed',
    vitals: { bp: '124/80', hr: '82', temp: '98.6', spo2: '99', weight: '160 lbs', bmi: '26.7' },
    problems: [{ name: 'Menometrorrhagia', icd: 'N92.1', status: 'Active' }],
    medications: [],
    obgynIntake: { gravida: 2, para: 2, lmp: daysAgoStr(38), gaWeeks: null, gaDays: null, edd: null, trimester: null, lastPap: '2024-09', papResult: 'Normal', concern: 'Irregular heavy bleeding x 3 mo' },
  },
  OB008: {
    firstName: 'Priya', lastName: 'Sharma', age: 34, gender: 'Female', dob: dobFromAgeYears(34),
    mrn: 'MRN-OB008', insurance: 'UHC', pcp: 'Dr. Ahmed',
    vitals: { bp: '114/72', hr: '74', temp: '98.4', spo2: '99', weight: '132 lbs', bmi: '22.0' },
    problems: [],
    medications: [{ name: 'Folic acid 400mcg', freq: 'Daily' }],
    obgynIntake: { gravida: 0, para: 0, lmp: daysAgoStr(12), gaWeeks: null, gaDays: null, edd: null, trimester: null, goal: 'Preconception — trying to conceive', carrierScreen: 'Ordered' },
  },
};

// ════════════════════════════════════════════════════════════════
// SURGICAL (general_surgery / orthopedics / urology / ent)
// ════════════════════════════════════════════════════════════════
export const SURGICAL_SCHEDULE = [
  { id: 'SG-001', patientId: 'SG001', name: 'Harold Bennett', time: '9:00 AM', type: 'Post-op', chief: 'POD 10 — right total knee arthroplasty f/u', status: 'completed' },
  { id: 'SG-002', patientId: 'SG002', name: 'Denise Park', time: '9:45 AM', type: 'Pre-op', chief: 'Pre-op — laparoscopic cholecystectomy in 2 wks', status: 'completed' },
  { id: 'SG-003', patientId: 'SG003', name: 'Vincent Ortiz', time: '10:30 AM', type: 'Consult', chief: 'Inguinal hernia — surgical consult', status: 'in_progress' },
  { id: 'SG-004', patientId: 'SG004', name: 'Margaret Shaw', time: '11:15 AM', type: 'Post-op', chief: 'POD 21 — lap chole f/u, wound check', status: 'waiting' },
  { id: 'SG-005', patientId: 'SG005', name: 'Jerome Walker', time: '1:00 PM', type: 'Pre-op', chief: 'Pre-op — ACL reconstruction next month', status: 'scheduled' },
  { id: 'SG-006', patientId: 'SG006', name: 'Patricia Holt', time: '1:45 PM', type: 'Post-op', chief: 'POD 42 — right shoulder rotator cuff repair', status: 'scheduled' },
  { id: 'SG-007', patientId: 'SG007', name: 'Kenneth Rowe', time: '2:30 PM', type: 'Consult', chief: 'Colonoscopy f/u — polyp path review', status: 'scheduled' },
  { id: 'SG-008', patientId: 'SG008', name: 'Linda Crawford', time: '3:15 PM', type: 'Pre-op', chief: 'Pre-op — umbilical hernia repair', status: 'scheduled' },
];

const SURGICAL_PATIENTS = {
  SG001: {
    firstName: 'Harold', lastName: 'Bennett', age: 68, gender: 'Male', dob: dobFromAgeYears(68),
    mrn: 'MRN-SG001', insurance: 'Medicare', pcp: 'Dr. Chen',
    vitals: { bp: '132/78', hr: '72', temp: '98.6', spo2: '98', weight: '215 lbs', bmi: '28.4' },
    problems: [{ name: 'S/p Right TKA', icd: 'Z96.651', status: 'Active', onset: daysAgoStr(10) }, { name: 'HTN', icd: 'I10', status: 'Active' }],
    medications: [{ name: 'Lisinopril 20mg', freq: 'Daily' }, { name: 'Apixaban 2.5mg', freq: 'BID', prescriber: 'Dr. Kumar', startDate: daysAgoStr(10) }],
    surgicalIntake: { asa: 'II', mets: 6, plannedProcedure: 'Right Total Knee Arthroplasty', cpt: '27447', surgeryDate: daysAgoStr(10), pod: 10, globalPeriod: '090 days', globalPeriodActive: true, anticoag: 'Apixaban — DVT ppx', wound: 'Healing, staples removed POD 14 (pending)', painScore: 3 },
  },
  SG002: {
    firstName: 'Denise', lastName: 'Park', age: 52, gender: 'Female', dob: dobFromAgeYears(52),
    mrn: 'MRN-SG002', insurance: 'BCBS', pcp: 'Dr. Chen',
    vitals: { bp: '126/76', hr: '78', temp: '98.6', spo2: '99', weight: '155 lbs', bmi: '25.8' },
    problems: [{ name: 'Cholelithiasis', icd: 'K80.20', status: 'Active' }],
    medications: [],
    surgicalIntake: { asa: 'I', mets: 8, plannedProcedure: 'Laparoscopic Cholecystectomy', cpt: '47562', surgeryDate: daysAgoStr(-14), pod: null, globalPeriod: '090 days', globalPeriodActive: false, anticoag: 'None', labs: 'CBC WNL, BMP WNL, INR 1.0', consentSigned: false },
  },
  SG003: {
    firstName: 'Vincent', lastName: 'Ortiz', age: 44, gender: 'Male', dob: dobFromAgeYears(44),
    mrn: 'MRN-SG003', insurance: 'UHC', pcp: 'Dr. Chen',
    vitals: { bp: '128/82', hr: '74', temp: '98.4', spo2: '99', weight: '190 lbs', bmi: '27.3' },
    problems: [{ name: 'Right inguinal hernia', icd: 'K40.90', status: 'Active' }],
    medications: [],
    surgicalIntake: { asa: 'I', mets: 10, plannedProcedure: 'Inguinal hernia repair (open vs lap) — TBD', cpt: '49505', surgeryDate: null, pod: null, globalPeriod: '090 days', globalPeriodActive: false, anticoag: 'None', consultStage: 'Initial consult — discuss options' },
  },
  SG004: {
    firstName: 'Margaret', lastName: 'Shaw', age: 58, gender: 'Female', dob: dobFromAgeYears(58),
    mrn: 'MRN-SG004', insurance: 'Aetna', pcp: 'Dr. Chen',
    vitals: { bp: '122/76', hr: '76', temp: '98.6', spo2: '99', weight: '165 lbs', bmi: '27.5' },
    problems: [{ name: 'S/p lap cholecystectomy', icd: 'Z98.890', status: 'Active', onset: daysAgoStr(21) }],
    medications: [],
    surgicalIntake: { asa: 'II', mets: 7, plannedProcedure: 'Laparoscopic Cholecystectomy', cpt: '47562', surgeryDate: daysAgoStr(21), pod: 21, globalPeriod: '090 days', globalPeriodActive: true, anticoag: 'None', wound: 'Healed, sutures dissolved', painScore: 1 },
  },
  SG005: {
    firstName: 'Jerome', lastName: 'Walker', age: 26, gender: 'Male', dob: dobFromAgeYears(26),
    mrn: 'MRN-SG005', insurance: 'Cigna', pcp: 'Dr. Chen',
    vitals: { bp: '118/72', hr: '64', temp: '98.4', spo2: '99', weight: '180 lbs', bmi: '24.4' },
    problems: [{ name: 'Left ACL tear', icd: 'S83.512A', status: 'Active' }],
    medications: [],
    surgicalIntake: { asa: 'I', mets: 12, plannedProcedure: 'ACL reconstruction', cpt: '29888', surgeryDate: daysAgoStr(-30), pod: null, globalPeriod: '090 days', globalPeriodActive: false, anticoag: 'None', labs: 'Pre-op pending', consentSigned: false },
  },
  SG006: {
    firstName: 'Patricia', lastName: 'Holt', age: 61, gender: 'Female', dob: dobFromAgeYears(61),
    mrn: 'MRN-SG006', insurance: 'Medicare', pcp: 'Dr. Chen',
    vitals: { bp: '130/80', hr: '74', temp: '98.6', spo2: '98', weight: '158 lbs', bmi: '26.3' },
    problems: [{ name: 'S/p rotator cuff repair', icd: 'Z98.890', status: 'Active', onset: daysAgoStr(42) }],
    medications: [],
    surgicalIntake: { asa: 'II', mets: 5, plannedProcedure: 'Right shoulder rotator cuff repair', cpt: '29827', surgeryDate: daysAgoStr(42), pod: 42, globalPeriod: '090 days', globalPeriodActive: true, anticoag: 'None', wound: 'Fully healed', painScore: 2, pt: 'Week 6 — ROM 120°/abduction' },
  },
  SG007: {
    firstName: 'Kenneth', lastName: 'Rowe', age: 55, gender: 'Male', dob: dobFromAgeYears(55),
    mrn: 'MRN-SG007', insurance: 'BCBS', pcp: 'Dr. Chen',
    vitals: { bp: '124/78', hr: '72', temp: '98.4', spo2: '99', weight: '195 lbs', bmi: '28.0' },
    problems: [{ name: 'Colon polyps, tubular adenoma', icd: 'D12.6', status: 'Active' }],
    medications: [],
    surgicalIntake: { asa: 'II', mets: 8, plannedProcedure: 'Post-polypectomy surveillance', cpt: '45378', surgeryDate: daysAgoStr(14), pod: 14, globalPeriod: '000 days', globalPeriodActive: false, anticoag: 'Aspirin 81mg — restarted', note: 'Path: 2 tubular adenomas, low-grade dysplasia; surveillance colonoscopy 3 yrs' },
  },
  SG008: {
    firstName: 'Linda', lastName: 'Crawford', age: 49, gender: 'Female', dob: dobFromAgeYears(49),
    mrn: 'MRN-SG008', insurance: 'Aetna', pcp: 'Dr. Chen',
    vitals: { bp: '120/74', hr: '70', temp: '98.6', spo2: '99', weight: '145 lbs', bmi: '24.2' },
    problems: [{ name: 'Umbilical hernia', icd: 'K42.9', status: 'Active' }],
    medications: [],
    surgicalIntake: { asa: 'I', mets: 9, plannedProcedure: 'Umbilical hernia repair', cpt: '49585', surgeryDate: daysAgoStr(-21), pod: null, globalPeriod: '090 days', globalPeriodActive: false, anticoag: 'None', labs: 'Pre-op pending', consentSigned: false },
  },
};

// ════════════════════════════════════════════════════════════════
// REGISTRY + LOOKUP
// ════════════════════════════════════════════════════════════════
const SURGICAL_MODES = new Set(['general_surgery', 'orthopedics', 'urology', 'ent']);

/**
 * Return the demo schedule for a given specialty id.
 * Returns null when the specialty has no custom schedule (caller should fall back).
 */
export const getScheduleForSpecialty = (specialtyId) => {
  if (specialtyId === 'psychiatry') return PSYCH_SCHEDULE;
  if (specialtyId === 'pediatrics') return PEDS_SCHEDULE;
  if (specialtyId === 'obgyn')      return OBGYN_SCHEDULE;
  if (SURGICAL_MODES.has(specialtyId)) return SURGICAL_SCHEDULE;
  return null;
};

/** Specialty-aware patient DB merged into getPatient path. */
export const getSpecialtyPatient = (apt) => {
  if (!apt?.patientId) return null;
  const id = apt.patientId;
  return PSYCH_PATIENTS[id] || PEDS_PATIENTS[id] || OBGYN_PATIENTS[id] || SURGICAL_PATIENTS[id] || null;
};
